import React, { useState } from 'react';

import { makeStyles } from 'tss-react/mui';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  ContentTemplate,
  ErrorInfo,
  Grid,
  Progress,
  SectionHeader,
} from 'web-library';
import { useMediaQuery, useTheme } from '@mui/material';
import { candidateService } from '../../../api';
import { useExamNavigation } from '../../../routes/ExamRoute';
import PersonalDetails from './particulars/PersonalDetails';
import ContactDetails from './particulars/ContactDetails';
import ExamDraftMessage from './ExamDraftMessage';
import { useUpdateRegistrationParticulars } from '../logic/useUpdateRegistrationParticulars';
import { GET_EXAM_CANDIDATE_PROFILE } from '../../../api/candidate/exam/@candidate/queries-services/keys';
import { CandidateProfileFormData } from '../../../models';
import NextOfKinContactDetails from './particulars/NextOfKinContactDetails';
import ParticularsSaveButtons from './particulars/ParticularsSaveButtons';
import ParticularsEditButtons from './particulars/ParticularsEditButtons';

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  addressError: {
    background: 'white'
  }
}));

interface ExamReviewPathParams {
  uuid?: string;
}

const ExamReviewParticulars = ({ title }: { title: string }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const history = useHistory();
  const { toExamReview } = useExamNavigation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { uuid = '' } = useParams<ExamReviewPathParams>();

  const [errorMsg, setErrorMsg] = useState<string>('');
  const [errorNonEditMsg, setErrorNonEditMsg] = useState<string>('');

  const onBack = () => {
    history.goBack();
  };

  const { data: candidateDetails } = candidateService.exam.candidate.useGetExamCandidateProfile(
    uuid
  );

  const confirmParticulars = () => {
    if (!candidateDetails?.localAddressPreferred) {
      setErrorNonEditMsg('Preferred Address must be a Singapore Address');
    } else {
      setErrorNonEditMsg('');
      history.push(toExamReview.replace(':uuid', uuid));
    }
  };

  const updateParticularHook = useUpdateRegistrationParticulars({
    particularDetails: candidateDetails,
    examUuid: uuid,
    queryKeyToInvalidateAfterUpdate: GET_EXAM_CANDIDATE_PROFILE,
    setErrorMsg,
    setReviewParticularErrorMessage: setErrorNonEditMsg,
  });

  if (updateParticularHook.isSettingUpFormValue || !candidateDetails) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      <Box>
        <ContentTemplate isContinuable type="full">
          <form
            onSubmit={updateParticularHook.form.handleSubmit(
              (formData: CandidateProfileFormData) => {
                setErrorMsg('');
                updateParticularHook.onSubmitHandler(formData);
              }
            )}
          >
            <SectionHeader
              title={title}
              componentDisplayBlock={isMobile && updateParticularHook.isEditState}>
              {
                  !updateParticularHook.isEditState && (
                      <ParticularsEditButtons {...updateParticularHook} />
                  )
              }
            </SectionHeader>
            <PersonalDetails
              examCandidateDetail={candidateDetails}
              {...updateParticularHook}
              defaultExpanded
            />
            <ContactDetails contactDetails={candidateDetails} {...updateParticularHook} />
            <NextOfKinContactDetails
              {...candidateDetails}
              accordionBottomLine={false}
              {...updateParticularHook}
              isLastItem
            />
            {updateParticularHook.isEditState && (
              <>
                <ErrorInfo label={errorMsg} />
                <ParticularsSaveButtons {...updateParticularHook} />
              </>
            )}
          </form>
        </ContentTemplate>
        <Grid container direction="row" alignItems="flex-start" spacing={3} className={classes.addressError}>
          {!updateParticularHook.isEditState && <ErrorInfo label={errorNonEditMsg || ''} />}
        </Grid>
        <ContentTemplate continuous>
          <Grid container direction="row" alignItems="flex-start" spacing={3}>
            <Grid xs={12} md={3}>
              <Button
                id="button-confirmParticulars-back"
                fullWidth
                // className={classes.button}
                variant="outlined"
                onClick={() => {
                  onBack();
                }}
              >
                {t('exam->>ReviewExam.Back')}
              </Button>
            </Grid>
            <Grid xs={12} md={4}>
              <Button
                id="button-confirmParticulars-proceed"
                fullWidth
                // className={classes.button}
                variant="contained"
                disabled={updateParticularHook.isEditState}
                onClick={() => {
                  confirmParticulars();
                }}
              >
                {t('exam->>ReviewExam.Confirm Particulars')}
              </Button>
            </Grid>
          </Grid>
          <ExamDraftMessage />
        </ContentTemplate>
      </Box>
    </>
  );
};
export default ExamReviewParticulars;
